import { useState, useEffect, useRef, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import api from '@utils/api';
import { useI18n } from '@/i18n';
import PageHeader from '@components/PageHeader';
import { ApprovalsTabs, StationBadge, EmployerBadge, Toast } from '@components/AbsenceBits';
import { fmtDate, fmtDays, kindLabel, apiMessage } from '@utils/absences';

const FIELD = 'input min-h-[44px] !text-base md:!text-sm';

export default function ApprovalInbox() {
  const { t } = useI18n();
  const { stations, pending, refreshCounts } = useOutletContext();
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const seq = useRef(0);

  const load = useCallback(() => {
    const mine = ++seq.current;
    return api.get('/absences/inbox').then(({ data }) => {
      if (mine !== seq.current) return;                 // out-of-order response
      setItems([...(data.requests || [])].sort((a, b) => (b.age_days ?? 0) - (a.age_days ?? 0)));
      setError('');
    }).catch(() => {
      if (mine !== seq.current) return;
      setError(t('appr.loadError'));
    });
  }, [t]);
  useEffect(() => { load(); }, [load]);

  // Optimistic removal only after the server accepted the decision.
  const decided = (id, text) => {
    setItems((list) => list.filter((r) => r.id !== id));
    setToast({ kind: 'ok', text });
    refreshCounts();
  };
  const conflict = () => {
    setToast({ kind: 'error', text: t('appr.alreadyDecided') });
    load(); refreshCounts();
  };
  const failed = (err) => {
    setToast({ kind: 'error', text: apiMessage(err, t('appr.actionFailed')) });
    load(); refreshCounts();
  };

  return (
    <>
      <PageHeader title={t('appr.title')} subtitle={t('appr.subtitle')} />
      <ApprovalsTabs stations={stations} pending={pending} />
      <div className="px-5 md:px-0 pb-6">
        <Toast toast={toast} />
        {error ? (
          <div role="alert" className="card p-6 text-sm text-red-700">{error}</div>
        ) : items === null ? (
          <div className="card p-8 text-center text-gray-500">{t('common.loading')}</div>
        ) : items.length === 0 ? (
          <div className="card p-8 text-center text-gray-600 text-sm" data-testid="inbox-empty">{t('appr.empty')}</div>
        ) : (
          <ul className="space-y-3">
            {items.map((r) => (
              <InboxCard key={r.id} r={r} onDecided={decided} onConflict={conflict} onFailed={failed} />
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

function InboxCard({ r, onDecided, onConflict, onFailed }) {
  const { t, lang } = useI18n();
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [comment, setComment] = useState('');
  const inFlight = useRef(false);

  const decide = async (event, body, okText) => {
    if (inFlight.current) return;                     // double-tap guard
    inFlight.current = true; setBusy(true);
    try {
      await api.post(`/absences/${r.id}/events`, { event, ...body });
      onDecided(r.id, okText);
    } catch (err) {
      inFlight.current = false; setBusy(false);
      if (err?.response?.status === 409) onConflict(); else onFailed(err);
    }
  };

  const range = r.start_date === r.end_date
    ? fmtDate(r.start_date, lang)
    : `${fmtDate(r.start_date, lang)} – ${fmtDate(r.end_date, lang)}`;
  const label = kindLabel(r, lang);

  return (
    <li className="card p-4" data-testid="inbox-card">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-base font-semibold text-glk-dark">{r.staff_name}</span>
        <StationBadge station={r.station} />
        <EmployerBadge employer={r.employer} />
      </div>
      <p className="mt-1 text-sm text-glk-dark">
        {label && <span className="font-medium">{label} · </span>}
        {range} · {t('abs.daysShort', { n: fmtDays(r.days, lang) })}
      </p>
      <p className="mt-0.5 text-xs text-gray-600">
        {t('appr.age', { n: r.age_days ?? 0 })}
        {' · '}
        {r.overlaps_team > 0 ? t('appr.overlap', { n: r.overlaps_team }) : t('appr.noOverlap')}
        {r.balance_after !== null && r.balance_after !== undefined ? ` · ${t('appr.balanceAfter', { n: fmtDays(r.balance_after, lang) })}` : ''}
      </p>
      {r.note && <p className="mt-2 text-sm text-gray-700 whitespace-pre-wrap">{r.note}</p>}

      {r.can_decide ? (
        rejecting ? (
          <div className="mt-3 space-y-2">
            <label className="label" htmlFor={`rej-${r.id}`}>{t('appr.rejectReason')}</label>
            <textarea id={`rej-${r.id}`} className={`${FIELD} resize-none`} rows={2} maxLength={2000} value={comment}
                      onChange={(e) => setComment(e.target.value)} />
            <div className="flex gap-2">
              <button type="button" className="btn-ghost flex-1 min-h-[44px]" disabled={busy} onClick={() => { setRejecting(false); setComment(''); }}>{t('appr.back')}</button>
              <button type="button" className="btn-primary flex-1 min-h-[44px] !bg-red-700 hover:!bg-red-800" disabled={busy || !comment.trim()}
                      onClick={() => decide('rejected', { comment: comment.trim() }, t('appr.rejected'))}>{t('appr.confirmReject')}</button>
            </div>
          </div>
        ) : (
          <div className="mt-3 flex gap-2">
            {r.ack_only ? (
              <button type="button" className="btn-primary flex-1 min-h-[44px]" disabled={busy}
                      onClick={() => decide('acknowledged', {}, t('appr.acknowledged'))}>{t('appr.acknowledge')}</button>
            ) : (
              <>
                <button type="button" className="btn-ghost flex-1 min-h-[44px]" disabled={busy} onClick={() => setRejecting(true)}>{t('appr.reject')}</button>
                <button type="button" className="btn-primary flex-1 min-h-[44px]" disabled={busy}
                        onClick={() => decide('approved', {}, t('appr.approved'))}>{t('appr.approve')}</button>
              </>
            )}
          </div>
        )
      ) : (
        <p className="mt-3 text-xs text-gray-600">{t('appr.cannotDecide')}</p>
      )}
    </li>
  );
}
